/**
 * Seating Map UX enhancements — shared by Chatrier + Lenglen sections.
 * Phase 1 (UX-IMPROVEMENT-PLAN-MAPS.md):
 *  - Mobile (<768px): groups sidebar ticket cards into category accordions
 *    (cheapest group expanded) — survives sidebar re-renders via MutationObserver.
 *  - Quantity filter chips (Any / 2 / 3 / 4+) — desktop + mobile, persists
 *    across map section-filtering.
 *  - Mobile sticky summary bar ("From $X · N listings" + View tickets) —
 *    hides while the listings are on screen so it never covers card CTAs.
 * Progressive enhancement only: without JS nothing here runs and the
 * server-rendered content (.fo-seat-summary) remains untouched.
 */
(function () {
  'use strict';

  function init() {
    var panel = document.getElementById('seating-map-tickets');
    if (!panel) return;

    var lang = (document.documentElement.lang || 'en').slice(0, 2);
    var isLenglen = !!document.getElementById('lenglen-map');
    var mqMobile = window.matchMedia('(max-width: 767px)');

    var CAT_META = {
      'box-loge': { color: '#4a9f4a', en: 'Box / Loge', fr: 'Loge / Box', es: 'Palco / Loge' },
      'cat-gold': { color: '#d67c38', en: 'Category Gold', fr: 'Catégorie Gold', es: 'Categoría Gold' },
      'cat-1': { color: '#e8c858', en: 'Category 1', fr: 'Catégorie 1', es: 'Categoría 1' },
      'cat-2': { color: isLenglen ? '#FF6B35' : '#FBCFD1', en: 'Category 2', fr: 'Catégorie 2', es: 'Categoría 2' },
      'cat-3': { color: '#52a5b8', en: 'Category 3', fr: 'Catégorie 3', es: 'Categoría 3' }
    };

    var STR = {
      en: { from: 'From', listing: 'listing', listings: 'listings', none: 'No listings match this quantity — tap “Any” to see all tickets.' },
      fr: { from: 'Dès', listing: 'offre', listings: 'offres', none: 'Aucune offre pour cette quantité — appuyez sur « Tous ».' },
      es: { from: 'Desde', listing: 'opción', listings: 'opciones', none: 'Ninguna opción para esa cantidad — toca “Todas”.' }
    };
    var T = STR[lang] || STR.en;

    var qtySelected = 0; // 0 = Any

    function fmtPrice(cents) {
      if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
        var eur = window.CurrencyConverter.convert(cents);
        if (eur) return eur;
      }
      return '$' + Math.round(cents / 100).toLocaleString('en-US');
    }

    function catName(cat) {
      var m = CAT_META[cat];
      if (m) return m[lang] || m.en;
      // Fallback: prettify handle-style category
      return (cat || 'Tickets').replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }

    function catColor(cat) {
      var m = CAT_META[cat];
      return m ? m.color : '#6C757D';
    }

    function getCards() {
      return Array.prototype.slice.call(panel.querySelectorAll('.ticket-variant-card'));
    }

    function cardMax(card) {
      var q = card.querySelector('.qty-input');
      var m = parseInt(q && q.max, 10);
      return isNaN(m) ? 99 : m;
    }

    function cardCat(card) {
      var c = (card.getAttribute('data-category') || '').toLowerCase();
      if (CAT_META[c]) return c;
      var t = card.textContent.toLowerCase();
      if (/\bbox\b|\bloge\b|\bpalco\b/.test(t)) return 'box-loge';
      if (/cat\s*gold/.test(t)) return 'cat-gold';
      if (/cat\s*1(?:\s|$|row)/.test(t) && !/cat\s*1\d/.test(t)) return 'cat-1';
      if (/cat\s*2/.test(t)) return 'cat-2';
      if (/cat\s*3/.test(t)) return 'cat-3';
      return c || 'other';
    }

    // Move cards back out of accordion wrappers (preserving order), drop wrappers
    function unwrapGroups() {
      Array.prototype.slice.call(panel.querySelectorAll('.smux-group')).forEach(function (g) {
        var body = g.querySelector('.smux-group-body');
        while (body && body.firstChild) {
          panel.insertBefore(body.firstChild, g);
        }
        g.remove();
      });
      var note = panel.querySelector('.smux-empty');
      if (note) note.remove();
    }

    function applyQtyFilter(cards) {
      var visible = [];
      cards.forEach(function (card) {
        var ok = qtySelected === 0 || cardMax(card) >= qtySelected;
        card.classList.toggle('smux-qty-hidden', !ok);
        if (ok) {
          visible.push(card);
          if (qtySelected > 0) {
            var q = card.querySelector('.qty-input');
            if (q) q.value = Math.min(qtySelected, cardMax(card));
          }
        }
      });
      return visible;
    }

    function enhance() {
      unwrapGroups();
      var cards = getCards();
      if (!cards.length) return;

      var visible = applyQtyFilter(cards);

      if (!visible.length) {
        var note = document.createElement('div');
        note.className = 'smux-empty';
        note.textContent = T.none;
        panel.insertBefore(note, panel.firstChild);
        return;
      }

      if (!mqMobile.matches) return; // desktop stays flat

      // Group visible cards by category
      var groups = {};
      var order = [];
      visible.forEach(function (card) {
        var cat = cardCat(card);
        if (!groups[cat]) {
          groups[cat] = { cards: [], min: Infinity };
          order.push(cat);
        }
        groups[cat].cards.push(card);
        var p = parseFloat(card.getAttribute('data-price'));
        if (!isNaN(p)) groups[cat].min = Math.min(groups[cat].min, p);
      });

      // Cheapest category first; the cheapest group starts expanded
      order.sort(function (a, b) { return groups[a].min - groups[b].min; });
      var cheapest = order[0];

      order.forEach(function (cat) {
        var g = groups[cat];
        var det = document.createElement('details');
        det.className = 'smux-group';
        det.dataset.category = cat;
        if (cat === cheapest || order.length === 1) det.open = true;

        var sum = document.createElement('summary');
        sum.className = 'smux-group-head';
        var n = g.cards.length;
        var listingsWord = n === 1 ? T.listing : T.listings;
        var priceText = isFinite(g.min) ? T.from + ' ' + fmtPrice(g.min) + ' · ' : '';
        sum.innerHTML =
          '<span class="smux-group-dot" style="background:' + catColor(cat) + '"></span>' +
          '<span class="smux-group-name">' + catName(cat) + '</span>' +
          '<span class="smux-group-meta">' + priceText + n + ' ' + listingsWord + '</span>' +
          '<svg class="smux-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
        det.appendChild(sum);

        var body = document.createElement('div');
        body.className = 'smux-group-body';
        g.cards.forEach(function (c) { body.appendChild(c); });
        det.appendChild(body);
        panel.appendChild(det);
      });
    }

    // Re-apply after every sidebar re-render (map filtering re-clones the cards)
    var obs = new MutationObserver(function () { safeEnhance(); });
    function safeEnhance() {
      obs.disconnect();
      try {
        enhance();
      } finally {
        obs.takeRecords();
        obs.observe(panel, { childList: true });
      }
    }
    obs.observe(panel, { childList: true });

    // ---- Quantity filter chips ----
    var chipWrap = document.getElementById('smux-qty-filter');
    if (chipWrap) {
      chipWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('.smux-chip');
        if (!btn) return;
        qtySelected = parseInt(btn.dataset.qty, 10) || 0;
        Array.prototype.slice.call(chipWrap.querySelectorAll('.smux-chip')).forEach(function (b) {
          var active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        safeEnhance();
      });
    }

    // Re-group when crossing the mobile breakpoint
    var lastMobile = mqMobile.matches;
    window.addEventListener('resize', function () {
      if (mqMobile.matches !== lastMobile) {
        lastMobile = mqMobile.matches;
        safeEnhance();
      }
    });

    // ---- Mobile sticky summary bar ----
    var sticky = document.getElementById('smux-sticky');
    if (sticky) {
      document.body.classList.add('smux-has-sticky'); // reserves bottom space (CSS)
      var stickyBtn = sticky.querySelector('.smux-sticky-btn');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function (e) {
          e.preventDefault();
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          // Hide the bar while the listings are on screen (never cover card CTAs)
          sticky.classList.toggle('smux-sticky--hidden', entries[0].isIntersecting);
        }, { threshold: 0 });
        io.observe(panel);
      }
    }

    // Refresh accordion header prices once the EUR rate arrives (FR locale)
    document.addEventListener('currency:ready', safeEnhance);

    // Initial pass (the map script has already populated the sidebar — both are deferred, this file loads after)
    safeEnhance();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
